/**
 * Centralized authenticated HTTP client.
 *
 * Everything that needs a bearer token goes through `authorizedApi`. Before this
 * existed, sixteen call sites each hand-built their own Authorization header and
 * each `catch` threw `error.response?.data`, which discarded the HTTP status —
 * so every screen was reduced to substring-matching English error prose to work
 * out whether a request had failed on auth. They matched inconsistently, and a
 * partner returning from Stripe KYC after the 60-minute access-token lifetime
 * saw the backend's raw wording in a toast.
 *
 * Two things fix that: tokens are refreshed here (proactively before expiry, and
 * reactively on a 401), and errors keep their `status`.
 */
import axios from "axios";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.BACKEND_URL ||
  "https://vw4a4w3lza.execute-api.us-east-1.amazonaws.com/";

// Refresh this far ahead of expiry. Long enough to cover a slow round trip,
// short enough that we aren't refreshing constantly.
const REFRESH_SKEW_MS = 2 * 60 * 1000;

// Fixed session cap. A login is good for this long and no longer: it equals the
// verified Cognito refresh-token validity (RefreshTokenValidity: 1 day, rotation
// off, on both app clients), so `login + SESSION_CAP_MS` is the real moment the
// server forces re-authentication. Change this one constant to change the policy.
export const SESSION_CAP_MS = 24 * 60 * 60 * 1000; // 24h
// The countdown banner appears once the remaining session drops inside this window.
export const SESSION_WARNING_MS = 5 * 60 * 1000; // 5 min

const KEYS = {
  access: "access_token",
  id: "id_token",
  refresh: "refresh_token",
  profile: "user_profile",
  userType: "user_type",
  businessProfileId: "business_profile_id",
  // Absolute ms-epoch deadline for the fixed session cap. Registered here on
  // purpose: clear() wipes every KEYS entry, so logout and the hard session-
  // expired path drop the cap with no extra code.
  sessionExpiry: "session_expires_at",
};

export const tokenStore = {
  getAccess: () => localStorage.getItem(KEYS.access),
  getId: () => localStorage.getItem(KEYS.id),
  getRefresh: () => localStorage.getItem(KEYS.refresh),

  /**
   * Write only the keys actually present. A refresh response has no
   * refresh_token — Cognito does not reissue one on REFRESH_TOKEN_AUTH — so a
   * naive assignment would wipe the only credential that can renew the session.
   */
  setSession: (session = {}) => {
    if (session.access_token) {
      localStorage.setItem(KEYS.access, session.access_token);
    }
    if (session.id_token) {
      localStorage.setItem(KEYS.id, session.id_token);
    }
    if (session.refresh_token) {
      localStorage.setItem(KEYS.refresh, session.refresh_token);
    }
    if (session.user_profile) {
      localStorage.setItem(KEYS.profile, JSON.stringify(session.user_profile));
    }
  },

  /**
   * Absolute ms-epoch when the fixed session cap expires, or null when unknown.
   * Null is meaningful (like getTokenExpiry): "no cap recorded" is not "expired".
   */
  getSessionExpiry: () => {
    const raw = localStorage.getItem(KEYS.sessionExpiry);
    const n = raw == null ? NaN : Number(raw);
    return Number.isFinite(n) ? n : null;
  },
  setSessionExpiry: (expiresAtMs) => {
    localStorage.setItem(KEYS.sessionExpiry, String(expiresAtMs));
  },
  /**
   * Start (or restart) the fixed cap from `now`. The single write choke point for
   * SESSION_CAP_MS — called at each real login, never by token refresh, so a
   * refreshed access token never extends the session.
   */
  beginSessionCap: (now = Date.now()) => {
    localStorage.setItem(KEYS.sessionExpiry, String(now + SESSION_CAP_MS));
  },

  clear: () => {
    Object.values(KEYS).forEach((key) => localStorage.removeItem(key));
  },
};

/**
 * Milliseconds-epoch expiry of a JWT, or null when it can't be read.
 *
 * Null is meaningful: the E2E fixtures seed opaque placeholder strings as the
 * access token, and callers must treat "unreadable" as "leave it alone" rather
 * than "expired".
 */
export const getTokenExpiry = (token) => {
  if (typeof token !== "string" || !token) return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch (_) {
    return null;
  }
};

export const isExpiringWithin = (token, windowMs = REFRESH_SKEW_MS) => {
  const expiry = getTokenExpiry(token);
  // Unreadable expiry: don't guess. Let the server decide and rely on the 401
  // path, rather than locking someone out over a token we simply can't parse.
  if (expiry === null) return false;
  return expiry - Date.now() <= windowMs;
};

/**
 * Flatten an axios failure into one shape that keeps the status code.
 *
 * `error` and `message` stay at the top level so the existing screens that read
 * `err?.error || err?.message` keep working untouched; `status` and `code` are
 * additive and are what new code should branch on.
 */
export const normalizeError = (error) => {
  const data = error?.response?.data;
  const base = data && typeof data === "object" ? { ...data } : {};
  const status = error?.response?.status ?? null;
  const message =
    base.error ||
    base.message ||
    (typeof data === "string" && data ? data : null) ||
    error?.message ||
    // A bare string throw: not something axios produces, but toApiError is
    // exported and older screens still defend against it.
    (typeof error === "string" && error ? error : null) ||
    "Request failed";

  return {
    ...base,
    error: message,
    message,
    status,
    isAuthError: status === 401,
  };
};

// --- session-expiry notification --------------------------------------------

let sessionExpiredListeners = [];

export const onSessionExpired = (listener) => {
  sessionExpiredListeners.push(listener);
  return () => {
    sessionExpiredListeners = sessionExpiredListeners.filter((l) => l !== listener);
  };
};

const notifySessionExpired = () => {
  tokenStore.clear();
  sessionExpiredListeners.forEach((listener) => {
    try {
      listener();
    } catch (_) {
      // A misbehaving listener must not stop the others running.
    }
  });
};

// --- refresh -----------------------------------------------------------------

/**
 * A real Error that also carries the normalized fields, so it satisfies
 * `toApiError`'s `"status" in err` check and every `err?.error` reader.
 */
const refreshError = (message, code, status = 401) =>
  Object.assign(new Error(message), {
    error: message,
    message,
    code,
    status,
    isAuthError: status === 401,
  });

let refreshInFlight = null;

const performRefresh = async () => {
  const refresh_token = tokenStore.getRefresh();
  const access_token = tokenStore.getAccess();

  if (!refresh_token || !access_token) {
    // Nothing to renew with, so there is genuinely no session to keep.
    notifySessionExpired();
    throw refreshError("No session to refresh.", "NO_SESSION");
  }

  let data;
  try {
    // Bare axios on purpose: routing this through `authorizedApi` would recurse
    // back into the interceptors on failure.
    const response = await axios.post(
      `${BACKEND_URL}api/auth/refresh`,
      { refresh_token, access_token },
      { headers: { "Content-Type": "application/json" } },
    );
    data = response?.data;
  } catch (error) {
    // Only a 4xx means the credentials themselves were rejected. A 5xx, a
    // timeout, or no response at all is the network or the server having a bad
    // moment — the refresh token is probably still good for days, and throwing
    // the session away over a blip strands a partner mid-onboarding, which is
    // the exact failure this module exists to prevent. The backend returns 502
    // REFRESH_FAILED for an upstream Cognito fault specifically so it is not
    // mistaken for a dead session; honour that here.
    const status = error?.response?.status;
    if (typeof status === "number" && status >= 400 && status < 500) {
      notifySessionExpired();
    }
    throw normalizeError(error);
  }

  if (!data?.access_token) {
    // A 200 with no token is a broken contract, not a rejected credential.
    throw refreshError("Refresh returned no token.", "REFRESH_FAILED", 502);
  }

  // Adopt a rotated refresh token when the backend returns one. With rotation
  // enabled, Cognito invalidates the old token after a short grace period, so
  // the client must switch to the new one. With rotation off, data.refresh_token
  // is undefined and setSession leaves the stored one untouched.
  tokenStore.setSession({
    access_token: data.access_token,
    id_token: data.id_token,
    refresh_token: data.refresh_token,
  });
  return data.access_token;
};

/**
 * Refresh the session, collapsing concurrent callers onto one request.
 *
 * The page a partner lands on when they come back from Stripe fires several
 * authenticated calls on mount, so without this they would each kick off their
 * own refresh and race to write the result.
 */
export const refreshSession = () => {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = performRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
};

// --- interceptors ------------------------------------------------------------

export const attachAuthHeader = async (config) => {
  if (config?._skipAuth) return config;

  let token = tokenStore.getAccess();

  if (token && isExpiringWithin(token)) {
    try {
      token = await refreshSession();
    } catch (_) {
      // Send the stale token anyway. A failed pre-emptive refresh shouldn't
      // turn a request that might still succeed into a guaranteed failure —
      // and if it really is dead, the 401 path handles it properly.
      token = tokenStore.getAccess() || token;
    }
  }

  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

export const handleResponseError = async (error) => {
  const status = error?.response?.status;
  const config = error?.config;

  if (status === 401 && config && !config.__isRetry) {
    try {
      const token = await refreshSession();
      config.__isRetry = true;
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
      return authorizedApi.request(config);
    } catch (refreshError) {
      return Promise.reject(toApiError(refreshError));
    }
  }

  return Promise.reject(normalizeError(error));
};

/**
 * Idempotent normalize. Errors coming out of `authorizedApi` have already been
 * through the response interceptor; running them through `normalizeError` again
 * would find no `.response`, reset `status` to null, and undo the whole point.
 */
export const toApiError = (err) =>
  err && typeof err === "object" && "status" in err ? err : normalizeError(err);

export const authorizedApi = axios.create({
  headers: { "Content-Type": "application/json" },
  withCredentials: false,
});

authorizedApi.interceptors.request.use(attachAuthHeader);
authorizedApi.interceptors.response.use((response) => response, handleResponseError);

export const __resetForTests = () => {
  refreshInFlight = null;
  sessionExpiredListeners = [];
};

export { BACKEND_URL };
